<script setup lang="ts">
/**
 * One extra address's own signature (#1122), where the address is listed: what mail that leaves from it is signed
 * with in place of the mailbox's, or "Uses the mailbox signature" while it has none. Written in the same editor as a
 * mail, saved or removed through `save` and `clear`, which the list it sits in points at its holder's routes.
 */
import { Button } from '@zerohuman/ui'
import { ref } from 'vue'
import type { MailAliasRow, MailAliasSignatureSave } from '@/api'
import MailEditor from '@/components/MailEditor.vue'
import { draftIsBlank, draftOfText, emptyDraft, type MailDraft } from '@/lib/mailDraft'

const props = defineProps<{
    alias: MailAliasRow
    editable: boolean
    save: (body: MailAliasSignatureSave) => Promise<unknown>
    clear: () => Promise<unknown>
}>()

const editing = ref(false)
const draft = ref<MailDraft>(emptyDraft())
const busy = ref(false)
const error = ref<string | null>(null)

/** `lead`, and the server's sentence after it when the refusal carried one: a bare code adds nothing. */
function failureText(caught: unknown, lead: string): string {
    const said = caught instanceof Error ? caught.message.trim() : ''
    return /\s/.test(said) ? `${lead} ${said}` : lead
}

/** What the editor opens with: the signature's HTML, or its text laid out as paragraphs for one written as text. */
function draftOf(signature: MailAliasRow['signature']): MailDraft {
    if (!signature) return emptyDraft()
    return signature.html === '' ? draftOfText(signature.text) : { html: signature.html, text: signature.text }
}

function start(): void {
    draft.value = draftOf(props.alias.signature)
    error.value = null
    editing.value = true
}

async function run(change: () => Promise<unknown>, lead: string): Promise<void> {
    if (busy.value) return
    busy.value = true
    error.value = null
    try {
        await change()
        editing.value = false
    } catch (caught) {
        error.value = failureText(caught, lead)
    } finally {
        busy.value = false
    }
}

function submit(): void {
    if (draftIsBlank(draft.value)) return
    void run(() => props.save({ html: draft.value.html, text: draft.value.text }), 'Could not save the signature.')
}

function remove(): void {
    void run(() => props.clear(), 'Could not remove the signature.')
}
</script>

<template>
	<div class="w-full space-y-2" data-testid="alias-signature">
		<template v-if="!editing">
			<div class="flex flex-wrap items-start gap-3">
				<pre v-if="alias.signature" class="text-muted-foreground border-border border-l-2 pl-3 font-sans text-xs whitespace-pre-wrap" data-alias-signature-preview>{{ alias.signature.text }}</pre>
				<p v-else class="text-muted-foreground text-xs" data-alias-signature-none>Uses the mailbox signature</p>
				<Button v-if="editable" size="sm" variant="outline" type="button" :aria-label="`Signature for ${alias.address}`" data-alias-signature-edit @click="start">
					Signature
				</Button>
			</div>
		</template>
		<form v-else class="space-y-2" data-alias-signature-form @submit.prevent="submit">
			<p class="text-muted-foreground text-xs">Mail sent from {{ alias.address }}, and replies that leave from it, carry this signature in place of the mailbox’s.</p>
			<MailEditor v-model="draft" :label="`Signature for ${alias.address}`" placeholder="A name, a title and a link" :disabled="busy" testid="alias-signature-editor" />
			<div class="flex flex-wrap gap-2">
				<Button type="submit" size="sm" :disabled="busy || draftIsBlank(draft)" data-alias-signature-save>Save signature</Button>
				<Button v-if="alias.signature" type="button" size="sm" variant="outline" :disabled="busy" data-alias-signature-remove @click="remove">Remove signature</Button>
				<Button type="button" size="sm" variant="ghost" :disabled="busy" @click="editing = false">Cancel</Button>
			</div>
		</form>
		<p v-if="error" role="alert" class="text-destructive text-sm" data-alias-signature-error>{{ error }}</p>
	</div>
</template>
